import _ from "lodash";
import DropdownTreeSelect from "react-dropdown-tree-select";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InputText, Text } from "@pentabd/ui";

import {
  InputDateControllerRow,
  InputTextRow,
  SelectController,
  SelectControllerRow,
} from "@components";

import { useBaseData } from "@hooks/useBaseData";
import { useLanguage } from "@hooks/useLanguage";
import { useMasterData } from "@hooks/useMasterData";

import {
  getConvertedPositionData,
  getConvertedPositionDataWithPostType,
  getConvertedUnitData,
  getConvertedUnitPositionData,
} from "@helpers/baseDataConvert";
import {
  getFilteredGradeData,
  getFilteredGradeDataForTransfer,
  getFilteredUnitPositionData,
  getFilteredUnitPositionDataForTransfer,
} from "@helpers/convertedGradeAndUnitPositionData";
import { getPositionColor } from "@helpers/getPositionColor";
import { unitTreeDataConvert } from "@helpers/unitTreeDataConvert";

import { BASE_DATA } from "@constants/base-data";

import { PositionType } from "@type/HRM/position";

import {
  TRANSFER,
  TransferDetailsFormDataType,
} from "@validations/HRM/transfer/transferValidation";
import { useEffect } from "react";
import { TransferDetailsType } from "@type/HRM/transfer";

const { WINGS, UNIT_TREE, POSITIONS, POST_TYPE, GRADES } = BASE_DATA;

interface BasicInfoProps {
  isDisabled: boolean;
  transfer: TransferDetailsType;
}

export const BasicInfo = ({ isDisabled, transfer }: BasicInfoProps) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const {
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<TransferDetailsFormDataType>();

  const { data: unitTree } = useBaseData(UNIT_TREE);
  const { data: wings } = useBaseData(WINGS);
  const { data: positions } = useBaseData(POSITIONS);
  const { data: postType } = useMasterData(POST_TYPE);
  const { data: grades } = useBaseData(GRADES);

  let wing = watch(TRANSFER.WING_DESK_IO_E0) as string;
  let designation = watch(TRANSFER.DESIGNATION) as string;

  const grade = watch(TRANSFER.GRADE) as number;

  useEffect(() => {
    if (grade) {
      setValue(
        TRANSFER.PAY_SCALE_MIN,
        {
          ..._.find(grades, (_grade) => {
            return _grade.id === grade;
          }),
        }.salaryScaleFrom,
      );
      setValue(
        TRANSFER.PAY_SCALE_MAX,
        {
          ..._.find(grades, (_grade) => {
            return _grade.id === grade;
          }),
        }.salaryScaleTo,
      );
    }
  }, [grade, grades, setValue]);

  return (
    <div className="d-flex flex-column gap-8 mb-8 pb-8">
      <InputDateControllerRow
        label="TRANSFER.BASIC_INFO.RELEASE_DATE"
        control={control}
        register={{ ...register(TRANSFER.RELEASE_DATE) }}
        errors={errors}
        name={TRANSFER.RELEASE_DATE}
        disabled={isDisabled}
        required
      />

      <div className="row">
        <div className="col-md-12 col-lg-3" />
        <div className="col-md-12 col-lg-7">
          <div className="d-flex flex-wrap gap-4">
            <div>
              <Controller
                control={control}
                name={TRANSFER.WING_DESK_IO_E0}
                render={({ field }) => {
                  return (
                    <DropdownTreeSelect
                      data={unitTreeDataConvert(
                        unitTree[0]?.children || [],
                        language,
                      )}
                      onChange={(e) =>
                        setValue(TRANSFER.WING_DESK_IO_E0, e.id as number)
                      }
                      mode="radioSelect"
                      texts={{
                        placeholder: t(
                          "EMPLOYEE.GENERAL_INFO.FORM.WING.PLACEHOLDER",
                        ),
                      }}
                      disabled={isDisabled}
                    />
                  );
                }}
              />
              {
                <Text size="sm" color="danger">
                  {errors?.gradeId && errors?.gradeId?.message}
                </Text>
              }
            </div>
            {wing && (
              <SelectController
                control={control}
                name={TRANSFER.WING_DESK_IO_E0}
                register={{ ...register(TRANSFER.WING_DESK_IO_E0) }}
                options={getConvertedUnitData(wings, language)}
                setValue={setValue}
                error={errors}
                disabled
              />
            )}
            {wing && (
              <SelectController
                control={control}
                name={TRANSFER.DESIGNATION}
                register={{ ...register(TRANSFER.DESIGNATION) }}
                error={errors}
                options={getConvertedPositionDataWithPostType(
                  transfer?.newGradeId || transfer?.newPositionId
                    ? positions
                        // .filter(
                        //   (position: PositionType) =>
                        //     position.unit.id === Number(wing),
                        // )
                        .map((position: PositionType) => ({
                          position: position.position,
                          postType: position.postType,
                        }))
                    : positions
                        .filter(
                          (position: PositionType) =>
                            position.unit.id === Number(wing),
                        )
                        .map((position: PositionType) => ({
                          position: position.position,
                          postType: position.postType,
                        })),
                  language,
                ).map((position: any) => ({
                  ...position,
                  customComponent: (
                    <Text
                      color={getPositionColor(postType as any, position as any)}
                    >
                      {position.label}
                    </Text>
                  ),
                }))}
                placeholder={t(
                  "EMPLOYEE.GENERAL_INFO.FORM.DESIGNATION.PLACEHOLDER",
                )}
                setValue={setValue}
                disabled={isDisabled}
              />
            )}

            {wing && designation ? (
              <>
                <SelectController
                  control={control}
                  name={TRANSFER.GRADE}
                  register={{ ...register(TRANSFER.GRADE) }}
                  error={errors}
                  options={
                    transfer?.newGradeId || transfer?.newPositionId
                      ? getFilteredGradeDataForTransfer(
                          positions,
                          wing,
                          designation,
                          language,
                        )
                      : getFilteredGradeData(
                          positions,
                          wing,
                          designation,
                          language,
                        )
                  }
                  placeholder={t(
                    "EMPLOYEE.GENERAL_INFO.FORM.GRADE.PLACEHOLDER",
                  )}
                  setValue={setValue}
                  disabled={isDisabled}
                />
                <SelectController
                  control={control}
                  name={TRANSFER.POSITION}
                  register={{ ...register(TRANSFER.POSITION) }}
                  error={errors}
                  options={
                    transfer?.newGradeId || transfer?.newPositionId
                      ? getFilteredUnitPositionDataForTransfer(
                          positions,
                          wing,
                          designation,
                          language,
                        )
                      : getFilteredUnitPositionData(
                          positions,
                          wing,
                          designation,
                          language,
                        )
                  }
                  placeholder={t(
                    "EMPLOYEE.GENERAL_INFO.FORM.POSITION.PLACEHOLDER",
                  )}
                  setValue={setValue}
                  disabled={isDisabled}
                />
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>

      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.WING"
        control={control}
        name={TRANSFER.WING_DESK_IO_E0}
        register={{ ...register(TRANSFER.WING_DESK_IO_E0) }}
        options={getConvertedUnitData(wings, language)}
        setValue={setValue}
        disabled
        required
      />

      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.DESIGNATION"
        control={control}
        name={TRANSFER.DESIGNATION}
        register={{ ...register(TRANSFER.DESIGNATION) }}
        options={getConvertedPositionData(
          positions
            // .filter(
            //   (position: PositionType) => position.unit.id === Number(wing),
            // )
            .map((position: PositionType) => position.position),
          language,
        )}
        setValue={setValue}
        disabled
        required
      />
      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.GRADE"
        control={control}
        name={TRANSFER.GRADE}
        register={{ ...register(TRANSFER.GRADE) }}
        options={getFilteredGradeDataForTransfer(
          positions,
          wing,
          designation,
          language,
        )}
        setValue={setValue}
        disabled
        required
      />
      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.POSITION"
        control={control}
        name={TRANSFER.POSITION}
        register={{ ...register(TRANSFER.POSITION) }}
        options={getConvertedUnitPositionData(positions, language)}
        setValue={setValue}
        disabled
        required
      />
      <InputDateControllerRow
        label="TRANSFER.BASIC_INFO.JOINING_DATE"
        control={control}
        register={{ ...register(TRANSFER.JOINING_DATE) }}
        errors={errors}
        name={TRANSFER.JOINING_DATE}
        required
      />

      <InputDateControllerRow
        label="TRANSFER.BASIC_INFO.TO_DATE"
        control={control}
        register={{ ...register(TRANSFER.TO_DATE) }}
        name={TRANSFER.TO_DATE}
      />

      <InputTextRow
        register={{ ...register(TRANSFER.BASIC_SALARY) }}
        label="TRANSFER.BASIC_INFO.BASIC_SALARY"
        error={errors}
      />

      <div className="row mb-8">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" color="title" size="sm">
            {t(
              "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.LABEL",
            )}
          </Text>
        </div>

        <div className="d-flex justify-content-center gap-9 align-items-center col-md-12 col-lg-7">
          <div className="flex-fill">
            <InputText
              {...register(TRANSFER.PAY_SCALE_MIN)}
              disabled
              suffix={
                <Text size="sm" color="subtitle1">
                  {t(
                    "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.PLACEHOLDER",
                  )}
                </Text>
              }
            />
          </div>
          <Text weight="semibold" color="title" size="sm">
            {t("EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.TO")}
          </Text>
          <div className="flex-fill">
            <InputText
              {...register(TRANSFER.PAY_SCALE_MAX)}
              disabled
              suffix={
                <Text size="sm" color="subtitle1">
                  {t(
                    "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.PLACEHOLDER",
                  )}
                </Text>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
